'use client';

import React, { ElementType, useEffect, useRef, useState, ComponentPropsWithoutRef } from 'react';

type MotionVariant = 'up' | 'fade' | 'scale';

type RevealOwnProps<E extends ElementType> = {
  as?: E;
  children: React.ReactNode;
  className?: string;
  delay?: number; // milliseconds
  once?: boolean;
  threshold?: number;
  rootMargin?: string;
  motion?: MotionVariant;
  revealOnMount?: boolean; // show immediately on page load with animation
};

type PolymorphicProps<E extends ElementType> = RevealOwnProps<E> &
  Omit<ComponentPropsWithoutRef<E>, keyof RevealOwnProps<E> | 'ref' | 'className' | 'children'>;

function RevealComponent<E extends ElementType = 'div'>({
  as,
  children,
  className = '',
  delay = 0,
  once = true,
  threshold = 0.2,
  rootMargin = '0px 0px -10% 0px',
  motion = 'up',
  revealOnMount = false,
  ...rest
}: PolymorphicProps<E>) {
  const ref = useRef<HTMLElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (revealOnMount) {
      // Ensure transition runs by scheduling after first paint
      const id = requestAnimationFrame(() => setIsVisible(true));
      return () => cancelAnimationFrame(id);
    }

    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            if (once) observer.unobserve(entry.target);
          } else if (!once) {
            setIsVisible(false);
          }
        });
      },
      { threshold, rootMargin }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [once, threshold, rootMargin, revealOnMount]);

  const Component = (as || 'div') as ElementType;
  const motionClass = motion === 'up' ? 'reveal-up' : motion === 'scale' ? 'reveal-scale' : 'reveal-fade';
  const classes = ['reveal', motionClass, isVisible ? 'is-visible' : '', className]
    .filter(Boolean)
    .join(' ');

  return (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <Component ref={ref as any} className={classes} style={{ ['--reveal-delay' as any]: `${delay}ms` }} {...rest}>
      {children}
    </Component>
  );
}

export default RevealComponent;


